/**
 * Native AppSettings shape (SensorContext.tsx) stored in user_profiles.alert_settings JSONB.
 * Real column: alert_phone (maps to alertPhone).
 */

export const UPDATE_FREQUENCY_OPTIONS = [5, 10, 15, 30, 60];
export const SYNC_INTERVAL_OPTIONS = [30, 60, 90, 120, 150, 180, 210, 240, 270, 300];
export const RETENTION_DAY_OPTIONS = [90, 180, 270, 365];
export const STATUS_TREND_WINDOWS = [
  { label: '5min', hours: 5 / 60 },
  { label: '10min', hours: 10 / 60 },
  { label: '30min', hours: 0.5 },
  { label: '1h', hours: 1 },
  { label: '2h', hours: 2 },
];
export const ZONE_TREND_OPTIONS = ['AGGRESSIVE', 'CONSERVATIVE', 'BALANCED'];

export const DEFAULT_APP_SETTINGS = {
  temperatureUnit: 'celsius',
  distanceUnit: 'm',
  updateFrequency: 5,
  syncInterval: 300,
  chartScales: { temperature: 2, humidity: 5 },
  alertsEnabled: false,
  alertPhone: '',
  alertCountryCode: '+1',
  tempAlertMinEnabled: false,
  tempAlertMaxEnabled: false,
  tempAlertMin: 5,
  tempAlertMax: 30,
  humidityAlertMinEnabled: false,
  humidityAlertMaxEnabled: false,
  humidityAlertMin: 30,
  humidityAlertMax: 80,
  pushNotificationsEnabled: false,
  pushTempAlertMinEnabled: false,
  pushTempAlertMaxEnabled: false,
  pushTempAlertMin: 5,
  pushTempAlertMax: 30,
  pushHumidityAlertMinEnabled: false,
  pushHumidityAlertMaxEnabled: false,
  pushHumidityAlertMin: 30,
  pushHumidityAlertMax: 80,
  statusTrendTimeWindow: 0.5,
  zoneTrendCalculation: 'AGGRESSIVE',
  temperatureThreshold: 0.5,
  humidityThreshold: 2.0,
  retentionEnabled: false,
  retentionDays: 90,
  // Web-only (stored in same JSONB)
  emailAlerts: true,
  statusAlerts: true,
  username: '',
};

const pick = (src, ...keys) => {
  for (const key of keys) {
    if (src[key] !== undefined && src[key] !== null) return src[key];
  }
  return undefined;
};

const coerceZoneTrend = (value) => {
  if (!value) return DEFAULT_APP_SETTINGS.zoneTrendCalculation;
  const upper = String(value).toUpperCase();
  if (ZONE_TREND_OPTIONS.includes(upper)) return upper;
  // Legacy web values
  if (value === 'average' || value === 'median' || value === 'weighted' || value === 'latest') {
    return 'CONSERVATIVE';
  }
  return DEFAULT_APP_SETTINGS.zoneTrendCalculation;
};

const coerceDistanceUnit = (value) => {
  if (value === 'm' || value === 'ft') return value;
  if (value === 'km') return 'm';
  if (value === 'miles' || value === 'mi') return 'ft';
  return DEFAULT_APP_SETTINGS.distanceUnit;
};

const coerceUpdateFrequency = (value) => {
  const n = Number(value);
  if (UPDATE_FREQUENCY_OPTIONS.includes(n)) return n;
  return DEFAULT_APP_SETTINGS.updateFrequency;
};

const coerceSyncInterval = (value) => {
  const n = Number(value);
  if (SYNC_INTERVAL_OPTIONS.includes(n)) return n;
  // Legacy: dataSyncFrequency in minutes
  if (n > 0 && n <= 60) return n * 60;
  return DEFAULT_APP_SETTINGS.syncInterval;
};

/** Normalize DB JSON + legacy keys → native AppSettings object. */
export const normalizeAppSettings = (raw, alertPhoneColumn = '') => {
  const src = raw && typeof raw === 'object' ? raw : {};

  const updateFrequency = coerceUpdateFrequency(
    pick(src, 'updateFrequency', 'streamingFrequency', 'streaming_frequency')
  );
  let syncInterval = coerceSyncInterval(pick(src, 'syncInterval', 'dataSyncFrequency', 'data_sync_frequency'));
  if (!pick(src, 'syncInterval', 'dataSyncFrequency', 'data_sync_frequency')) {
    syncInterval = updateFrequency * 60;
  }

  const chartScales = {
    temperature: pick(src, 'chartScales')?.temperature ?? src.chartTemperatureScale ?? 2,
    humidity: pick(src, 'chartScales')?.humidity ?? src.chartHumidityScale ?? 5,
  };

  // Hydrate push thresholds from native AlertMonitoringService cloud keys when
  // full push* fields are not yet stored (native only syncs temperatureMin/Max…).
  const hasCloudPushThresholds =
    src.temperatureMin !== undefined ||
    src.temperatureMax !== undefined ||
    src.humidityMin !== undefined ||
    src.humidityMax !== undefined;

  const pushTempAlertMin = Number(
    pick(src, 'pushTempAlertMin') ?? (hasCloudPushThresholds ? src.temperatureMin : undefined) ?? 5
  );
  const pushTempAlertMax = Number(
    pick(src, 'pushTempAlertMax') ?? (hasCloudPushThresholds ? src.temperatureMax : undefined) ?? 30
  );
  const pushHumidityAlertMin = Number(
    pick(src, 'pushHumidityAlertMin') ?? (hasCloudPushThresholds ? src.humidityMin : undefined) ?? 30
  );
  const pushHumidityAlertMax = Number(
    pick(src, 'pushHumidityAlertMax') ?? (hasCloudPushThresholds ? src.humidityMax : undefined) ?? 80
  );

  return {
    ...DEFAULT_APP_SETTINGS,
    ...src,
    temperatureUnit: pick(src, 'temperatureUnit', 'temperature_unit') === 'fahrenheit' ? 'fahrenheit' : 'celsius',
    distanceUnit: coerceDistanceUnit(pick(src, 'distanceUnit', 'distance_unit')),
    updateFrequency,
    syncInterval,
    chartScales,
    alertsEnabled: !!(pick(src, 'alertsEnabled', 'smsAlerts', 'sms_alerts') ?? DEFAULT_APP_SETTINGS.alertsEnabled),
    alertPhone: alertPhoneColumn || pick(src, 'alertPhone', 'alert_phone') || '',
    alertCountryCode: pick(src, 'alertCountryCode', 'alert_country_code') || '+1',
    tempAlertMinEnabled: !!(pick(src, 'tempAlertMinEnabled', 'temp_alert_min_enabled') ?? false),
    tempAlertMaxEnabled: !!(pick(src, 'tempAlertMaxEnabled', 'temp_alert_max_enabled') ?? false),
    tempAlertMin: Number(pick(src, 'tempAlertMin', 'temp_low_trigger', 'temperatureMin') ?? 5),
    tempAlertMax: Number(pick(src, 'tempAlertMax', 'temp_high_trigger', 'temperatureMax') ?? 30),
    humidityAlertMinEnabled: !!(pick(src, 'humidityAlertMinEnabled') ?? false),
    humidityAlertMaxEnabled: !!(pick(src, 'humidityAlertMaxEnabled') ?? false),
    humidityAlertMin: Number(pick(src, 'humidityAlertMin', 'humidity_low_trigger', 'humidityMin') ?? 30),
    humidityAlertMax: Number(pick(src, 'humidityAlertMax', 'humidity_high_trigger', 'humidityMax') ?? 80),
    pushNotificationsEnabled: !!(
      pick(src, 'pushNotificationsEnabled', 'pushNotifications', 'push_notifications') ??
      (hasCloudPushThresholds ? true : false)
    ),
    pushTempAlertMinEnabled: !!(
      pick(src, 'pushTempAlertMinEnabled') ?? (src.temperatureMin !== undefined)
    ),
    pushTempAlertMaxEnabled: !!(
      pick(src, 'pushTempAlertMaxEnabled') ?? (src.temperatureMax !== undefined)
    ),
    pushTempAlertMin,
    pushTempAlertMax,
    pushHumidityAlertMinEnabled: !!(
      pick(src, 'pushHumidityAlertMinEnabled') ?? (src.humidityMin !== undefined)
    ),
    pushHumidityAlertMaxEnabled: !!(
      pick(src, 'pushHumidityAlertMaxEnabled') ?? (src.humidityMax !== undefined)
    ),
    pushHumidityAlertMin,
    pushHumidityAlertMax,
    statusTrendTimeWindow: Number(pick(src, 'statusTrendTimeWindow') ?? 0.5),
    zoneTrendCalculation: coerceZoneTrend(pick(src, 'zoneTrendCalculation', 'zone_trend_calculation')),
    temperatureThreshold: Number(pick(src, 'temperatureThreshold') ?? 0.5),
    humidityThreshold: Number(pick(src, 'humidityThreshold') ?? 2.0),
    retentionEnabled: !!(pick(src, 'retentionEnabled') ?? false),
    retentionDays: Number(pick(src, 'retentionDays') ?? 90),
    emailAlerts: !!(pick(src, 'emailAlerts', 'email_alerts') ?? true),
    statusAlerts: !!(pick(src, 'statusAlerts', 'status_alerts') ?? true),
    username: pick(src, 'username') || '',
  };
};

/** Merge partial patch into settings (native updateSettings semantics). */
export const mergeAppSettings = (current, patch) => {
  const next = { ...current, ...patch };
  if (patch.chartScales) {
    next.chartScales = { ...current.chartScales, ...patch.chartScales };
  }
  if (patch.updateFrequency !== undefined) {
    next.updateFrequency = coerceUpdateFrequency(patch.updateFrequency);
    if (patch.syncInterval === undefined) {
      next.syncInterval = next.updateFrequency * 60;
    }
  }
  if (patch.syncInterval !== undefined) {
    next.syncInterval = coerceSyncInterval(patch.syncInterval);
  }
  if (patch.zoneTrendCalculation !== undefined) {
    next.zoneTrendCalculation = coerceZoneTrend(patch.zoneTrendCalculation);
  }
  if (patch.distanceUnit !== undefined) {
    next.distanceUnit = coerceDistanceUnit(patch.distanceUnit);
  }
  return next;
};

/** AlertMonitoringService thresholds (native push notification sync). */
export const buildPushAlertThresholds = (settings) => ({
  temperatureMin: settings.pushTempAlertMinEnabled ? settings.pushTempAlertMin : undefined,
  temperatureMax: settings.pushTempAlertMaxEnabled ? settings.pushTempAlertMax : undefined,
  humidityMin: settings.pushHumidityAlertMinEnabled ? settings.pushHumidityAlertMin : undefined,
  humidityMax: settings.pushHumidityAlertMaxEnabled ? settings.pushHumidityAlertMax : undefined,
});

/** Serialize AppSettings → alert_settings JSONB (includes push thresholds for native monitor). */
export const serializeAppSettings = (settings) => {
  const pushThresholds = settings.pushNotificationsEnabled
    ? buildPushAlertThresholds(settings)
    : {};

  return {
    ...settings,
    ...pushThresholds,
  };
};
